// Vault key hierarchy (§7.1), all in the browser via the WASM crypto core (§4).
// The backend only ever holds wrapped key material; plaintext keys live here, in memory.
import { wasm } from "./core";

// Argon2id profile ii (web-mandatory, §6): t=3, m=64 MiB, p=4.
export const KDF_PROFILE_II = 2;
export const KDF_MEM_KIB = 65536;
export const KDF_TIME = 3;
export const KDF_PAR = 4;

const enc = new TextEncoder();
// Domain-separation AAD/info labels (must match between wrap and unwrap; this client
// owns both sides). Org/item AAD is empty because server-assigned ids are unknown at
// creation time — domain separation comes from the wrap layer + HPKE info.
const AAD_USER_KEY = enc.encode("vault/user-key");
const AAD_PRIVATE_KEY = enc.encode("vault/user-private-key");
const INFO_ORG_KEY_WRAP = enc.encode("vault/v1/org-key-wrap");
const EMPTY = new Uint8Array(0);

export function b64decode(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function b64encode(b: Uint8Array): string {
  let s = "";
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
  return btoa(s);
}

/** Derive the Master key from the master password + salt (Argon2id profile ii). */
export function deriveMasterKey(password: string, salt: Uint8Array): Uint8Array {
  return wasm.argon2idDerive(KDF_PROFILE_II, enc.encode(password), salt, 32);
}

export interface EnrollMaterial {
  publicKey: Uint8Array;
  wrappedUserKey: Uint8Array;
  wrappedPrivateKey: Uint8Array;
  salt: Uint8Array;
}

/** Build fresh enrollment material from a chosen master password (§7.2). */
export function buildEnrollment(password: string): EnrollMaterial {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const masterKey = deriveMasterKey(password, salt);
  const userKey = wasm.generateKey();
  const kp = wasm.generateKeypair();
  const wrappedUserKey = wasm.wrapSymmetric(masterKey, userKey, AAD_USER_KEY);
  const wrappedPrivateKey = wasm.wrapSymmetric(userKey, kp.privateKey, AAD_PRIVATE_KEY);
  return { publicKey: kp.publicKey, wrappedUserKey, wrappedPrivateKey, salt };
}

export interface UnlockedKeys {
  userKey: Uint8Array;
  privateKey: Uint8Array;
}

/** Unlock: derive Master key, unwrap User key, then unwrap the HPKE private key.
 *  Throws if the master password is wrong (AEAD authentication fails). */
export function unlockKeys(
  password: string,
  salt: Uint8Array,
  wrappedUserKey: Uint8Array,
  wrappedPrivateKey: Uint8Array,
): UnlockedKeys {
  const masterKey = deriveMasterKey(password, salt);
  const userKey = wasm.unwrapSymmetric(masterKey, wrappedUserKey, AAD_USER_KEY);
  const privateKey = wasm.unwrapSymmetric(userKey, wrappedPrivateKey, AAD_PRIVATE_KEY);
  return { userKey, privateKey };
}

/** Unwrap just the User key from its master-wrapped blob — used to verify a master password
 *  (throws if the password is wrong: the AEAD tag won't match). */
export function unwrapUserKey(password: string, salt: Uint8Array, wrappedUserKey: Uint8Array): Uint8Array {
  const masterKey = deriveMasterKey(password, salt);
  return wasm.unwrapSymmetric(masterKey, wrappedUserKey, AAD_USER_KEY);
}

export interface MasterRewrap {
  wrappedUserKey: Uint8Array;
  salt: Uint8Array;
}

/** Re-wrap the (unchanged) User key under a key derived from a NEW master password, with a fresh
 *  salt — this is all a master-password change does (§7.2). The keypair, org keys and item keys are
 *  untouched because only the User-key wrap depends on the master password. */
export function rewrapUserKey(userKey: Uint8Array, newPassword: string): MasterRewrap {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const masterKey = deriveMasterKey(newPassword, salt);
  const wrappedUserKey = wasm.wrapSymmetric(masterKey, userKey, AAD_USER_KEY);
  return { wrappedUserKey, salt };
}

/** Unwrap an org_key (HPKE) using the user's private key. */
export function unwrapOrgKey(privateKey: Uint8Array, wrappedOrgKey: Uint8Array): Uint8Array {
  return wasm.kemOpen(privateKey, INFO_ORG_KEY_WRAP, EMPTY, wrappedOrgKey);
}

/** Wrap an org_key onto a member's public key (HPKE) — used when creating an org. */
export function wrapOrgKeyTo(publicKey: Uint8Array, orgKey: Uint8Array): Uint8Array {
  return wasm.kemSeal(publicKey, INFO_ORG_KEY_WRAP, EMPTY, orgKey);
}

/** Generate a new org_key. */
export function newOrgKey(): Uint8Array {
  return wasm.generateKey();
}

/** Generate an HPKE keypair for org recovery (§17.1). The private key is shown to the owner once
 *  and kept offline; the server only ever sees the public key + the org_key wrapped onto it. */
export function newRecoveryKeypair(): { publicKey: Uint8Array; privateKey: Uint8Array } {
  return wasm.generateKeypair();
}

// --- Items ---

/** Generate a new item_key and wrap it under the org_key. */
export function newWrappedItemKey(orgKey: Uint8Array): { itemKey: Uint8Array; wrapped: Uint8Array } {
  const itemKey = wasm.generateKey();
  return { itemKey, wrapped: wasm.wrapSymmetric(orgKey, itemKey, EMPTY) };
}

export function unwrapItemKey(orgKey: Uint8Array, wrapped: Uint8Array): Uint8Array {
  return wasm.unwrapSymmetric(orgKey, wrapped, EMPTY);
}

export function encryptItem(itemKey: Uint8Array, plaintext: object): Uint8Array {
  return wasm.itemEncrypt(itemKey, EMPTY, enc.encode(JSON.stringify(plaintext)));
}

export function decryptItem<T>(itemKey: Uint8Array, blob: Uint8Array): T {
  const bytes = wasm.itemDecrypt(itemKey, EMPTY, blob);
  return JSON.parse(new TextDecoder().decode(bytes)) as T;
}

// Folder names are encrypted with the org_key (AES-256-GCM, §19).
export function encryptName(orgKey: Uint8Array, name: string): Uint8Array {
  return wasm.itemEncrypt(orgKey, EMPTY, enc.encode(name));
}
export function decryptName(orgKey: Uint8Array, blob: Uint8Array): string {
  return new TextDecoder().decode(wasm.itemDecrypt(orgKey, EMPTY, blob));
}

// Device-node metadata (category/address/…) is encrypted with the org_key, like a name (§19).
export function encryptMeta(orgKey: Uint8Array, meta: object): Uint8Array {
  return wasm.itemEncrypt(orgKey, EMPTY, enc.encode(JSON.stringify(meta)));
}
export function decryptMeta<T>(orgKey: Uint8Array, blob: Uint8Array): T {
  return JSON.parse(new TextDecoder().decode(wasm.itemDecrypt(orgKey, EMPTY, blob))) as T;
}

// --- Item-share (§12) ---

/** Wrap an item_key (HPKE) onto a recipient's public key for sharing. */
export function shareItemKeyTo(recipientPublicKey: Uint8Array, itemKey: Uint8Array): Uint8Array {
  return wasm.shareCreate(recipientPublicKey, itemKey, EMPTY);
}

/** Recover a shared item_key using the recipient's private key. */
export function acceptSharedItemKey(privateKey: Uint8Array, wrapped: Uint8Array): Uint8Array {
  return wasm.shareAccept(privateKey, wrapped, EMPTY);
}
